<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { camera_key_prompt } from '../stores/camera_key_prompt';
	import { recoverMasterKey } from '../helpers/encryption/master-key-management';
	import { ButtonV2, CircleAvatar, Container, Input, Modal, Text } from '@Shelly/shelly-design-system';

	/** The camera this view shows; only its owner can unlock its recordings. */
	export let device: Device;

	/** Minimum length of the recording password (matches provisioning). */
	const MIN_PASSWORD_LENGTH = 8;

	let passphrase = '';
	let inputError = '';
	let loading = false;

	// The single, shared prompt is driven entirely by the trigger store, so any
	// top-level camera view can render one instance and any caller can open it.
	$: promptState = $camera_key_prompt;
	$: deviceId = promptState.deviceId;
	$: date = promptState.date;
	// A guest of a shared camera holds no master key, so there is nothing to unlock.
	$: showModal = promptState.open && !device?.shared;

	/** A wrong/rotated-key error pushed by the player when the era key fails to decrypt. */
	$: wrongKeyError = promptState.wrongKey
		? $format('camera_recording_key_wrong', { default: 'Wrong password. Please try again.' })
		: '';
	$: error = inputError || wrongKeyError;

	const clearError = () => {
		inputError = '';
	};

	const resetState = () => {
		passphrase = '';
		inputError = '';
		loading = false;
	};

	const handleSubmit = async () => {
		if (passphrase.length < MIN_PASSWORD_LENGTH) {
			inputError = $format('camera_recording_key_invalid', {
				default: 'The password must be at least 8 characters.',
			});
			return;
		}

		loading = true;
		// Recover for the era being unlocked so the resolved key matches this
		// recording; a correct password still preserves every same-password era.
		const recovered = await recoverMasterKey(deviceId, passphrase, date);
		loading = false;

		if (!recovered) {
			inputError = $format('camera_recording_key_wrong', {
				default: 'Wrong password. Please try again.',
			});
			return;
		}

		resetState();
		camera_key_prompt.close();
	};

	const handleClose = () => {
		resetState();
		camera_key_prompt.close();
	};
</script>

{#if showModal}
	<Modal
		{showModal}
		on:close={handleClose}
		title={$format('camera_unlock_title', { default: 'Unlock Camera Cloud Recordings' })}
		backdropClose
	>
		<Container slot="content" class="pb-0" padding="scrollPaddings">
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					size="xxl"
					icon="fa-thin fa-clapperboard-play"
					borderStatus={{ icon: 'fa-solid fa-lock', color: 'color-primary' }}
				/>
			</div>
			<Text class="mb-m text-center">
				{$format('camera_unlock_description', {
					default:
						'Enter your password to unlock these cloud recordings. Only your password can decrypt them.',
				})}
			</Text>
			<Text styleType="body" class="mb-s">
				{$format('camera_recording_key_label', { default: 'Encryption password' })}
			</Text>
			<Input
				id="camera-recording-key"
				aria-labelledby="camera-recording-key"
				bind:value={passphrase}
				on:input={clearError}
				{error}
				size="l"
				type="password"
				label={$format('camera_recording_key_placeholder', { default: 'Enter password' })}
				class="width-full"
				a11yProps={{
					iconButtonAriaLabel: $format('button_show_password', {
						default: 'Show password',
					}),
				}}
			/>
		</Container>
		<div slot="footer" class="d-flex justify-content-end">
			<ButtonV2
				class="mr-s"
				variant="secondary"
				disabled={loading}
				on:click={handleClose}
				aria-label={$format('button_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				{loading}
				disabled={!passphrase || loading}
				on:click={handleSubmit}
				aria-label={$format('camera_recording_key_submit', { default: 'Unlock' })}
			>
				{$format('camera_recording_key_submit', { default: 'Unlock' })}
			</ButtonV2>
		</div>
	</Modal>
{/if}
